/**
 * The languages the practice voice bot can actually speak.
 *
 * The app's translate picker offers 105 — that list is for translating the UI, and the voice bot
 * supports 59 of them. Offering the rest let a learner choose a language the session would quietly
 * drop back to the Cycle's own, with nothing on screen to say why.
 *
 * Ordered by the name that is read on screen, not by code, because the picker renders the rows in
 * the order they are given here and a learner looks for "Bosnian", not for `bs`. The picker pins
 * the language the session would run in above this list, so the order here is the order of
 * everything else.
 *
 * Same `[code, name]` shape as LANGUAGES, so the existing picker renders it unchanged. Three codes
 * are spelled differently from the translate list and are corrected here, since what is sent has to
 * be what the voice API accepts:
 *
 *   Khmer    translate `km`     → voice `khm`
 *   Chinese  translate `zh-CN`  → voice `zh`
 *   Hebrew   `he` — the API also accepts the legacy `iw`, so one row is enough
 */
export const PRACTICE_LANGUAGES: Array<[string, string]> = [
  ['af', 'Afrikaans'],
  ['ar', 'Arabic'],
  ['hy', 'Armenian'],
  ['az', 'Azerbaijani'],
  ['be', 'Belarusian'],
  ['bs', 'Bosnian'],
  ['bg', 'Bulgarian'],
  ['ca', 'Catalan'],
  ['zh', 'Chinese'],
  ['hr', 'Croatian'],
  ['cs', 'Czech'],
  ['da', 'Danish'],
  ['nl', 'Dutch'],
  ['en', 'English'],
  ['et', 'Estonian'],
  ['fi', 'Finnish'],
  ['fr', 'French'],
  ['gl', 'Galician'],
  ['de', 'German'],
  ['el', 'Greek'],
  ['he', 'Hebrew'],
  ['hi', 'Hindi'],
  ['hu', 'Hungarian'],
  ['is', 'Icelandic'],
  ['id', 'Indonesian'],
  ['it', 'Italian'],
  ['ja', 'Japanese'],
  ['kn', 'Kannada'],
  ['kk', 'Kazakh'],
  // ['khm', 'Khmer'],
  ['ko', 'Korean'],
  ['lv', 'Latvian'],
  ['lt', 'Lithuanian'],
  ['mk', 'Macedonian'],
  ['ms', 'Malay'],
  ['mi', 'Maori'],
  ['mr', 'Marathi'],
  ['ne', 'Nepali'],
  ['no', 'Norwegian'],
  ['fa', 'Persian'],
  ['pl', 'Polish'],
  ['pt', 'Portuguese'],
  ['ro', 'Romanian'],
  ['ru', 'Russian'],
  ['sr', 'Serbian'],
  ['sk', 'Slovak'],
  ['sl', 'Slovenian'],
  ['es', 'Spanish'],
  ['sw', 'Swahili'],
  ['sv', 'Swedish'],
  ['tl', 'Tagalog'],
  ['ta', 'Tamil'],
  ['th', 'Thai'],
  ['tr', 'Turkish'],
  ['uk', 'Ukrainian'],
  ['ur', 'Urdu'],
  ['vi', 'Vietnamese'],
  ['cy', 'Welsh'],
];

/**
 * The voice API's spelling for a code the rest of the app uses.
 *
 * The Cycle's own language arrives from the path (`km`, `zh-CN`, `iw`), and that is what the
 * session would be started with unless it is translated on the way out.
 */
const VOICE_LANGUAGE_ALIASES: { [code: string]: string } = {
  // km: 'khm',
  'zh-CN': 'zh',
  'zh-TW': 'zh',
  iw: 'he',
};

/** The code to send for a language, or '' when the bot cannot speak it. */
export function toVoiceLanguage(code: string): string {
  const wanted = (code || '').trim();
  if (!wanted) { return ''; }
  const mapped = VOICE_LANGUAGE_ALIASES[wanted] || wanted;
  return PRACTICE_LANGUAGES.some(([value]) => value === mapped) ? mapped : '';
}
